import { DownOutlined, RightOutlined } from "@ant-design/icons"
import { Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import type {
  ComparisonDiffEntry,
  ComparisonGroup
} from "./comparison-grouped-value.types.ts"

const { Text } = Typography

interface GroupedComparisonCellProps {
  /** Never re-flattened across groups — each group renders (and collapses)
   * independently, mirroring the real Project-Type structure the source
   * data already carries. */
  groups: ComparisonGroup[]
  /** `undefined` — nothing to diff this column against. `[]` — diffed, and
   * identical: drives the "✓ ... match" collapsed state. Non-empty — the
   * "View changes" affordance appears. */
  diff?: ComparisonDiffEntry[]
  /** Already-translated, field-specific ("✓ Scope structure and milestones
   * match" / "✓ Payment terms match") — shown only when `diff` is `[]`. */
  identicalMessage: string
  /** Already-translated nouns ("milestone"/"milestones",
   * "payment"/"payments") used in every count string this component
   * renders. */
  itemNounSingular: string
  itemNounPlural: string
  testIdPrefix: string
}

function totalItemCount(groups: ComparisonGroup[]) {
  return groups.reduce((sum, group) => sum + group.items.length, 0)
}

/**
 * Collapsed-by-default cell for a "list of named, priced items" field —
 * Scope (milestones) and Payment Terms (payment schedule) share this one
 * component rather than two parallel builds, since both are the same shape:
 * items grouped under a name, optionally diffed against another source by
 * stable id. Fixes the "32 vs 35 milestones renders as a wall of text"
 * problem the plain bulleted `FieldValueLines` rendering had for these two
 * fields specifically — every other field keeps that simpler path.
 *
 * Three states, all independently toggleable: the collapsed summary (always
 * shown), "View changes" (only what differs — added/removed/changed, by id,
 * never a string diff), and "View all {items}" (the complete structure,
 * grouped, each group collapsible on its own — never flattened). Both
 * expansions cap their own height and scroll internally
 * (`GroupedComparisonCell.i18n.test.tsx`/layout can't prove this in jsdom —
 * verified in a real browser, see the PR) so a long list never grows the
 * modal itself past its own capped height.
 */
function GroupedComparisonCell({
  groups,
  diff,
  identicalMessage,
  itemNounSingular,
  itemNounPlural,
  testIdPrefix
}: GroupedComparisonCellProps) {
  const { t } = useTranslation()
  const [showChanges, setShowChanges] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [openGroupIds, setOpenGroupIds] = useState<Set<string>>(
    () => new Set(groups[0] ? [groups[0].id] : [])
  )

  if (groups.length === 0) return null

  const total = totalItemCount(groups)
  const isIdentical = diff !== undefined && diff.length === 0
  const hasDiff = !!diff && diff.length > 0

  const toggleGroup = (id: string) => {
    setOpenGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const summaryText =
    groups.length === 1
      ? t("documentSync.groupedComparison.singleGroupSummary", {
          groupName: groups[0].name,
          count: total,
          item: total === 1 ? itemNounSingular : itemNounPlural
        })
      : t("documentSync.groupedComparison.multiGroupSummary", {
          count: groups.length,
          total,
          item: itemNounPlural
        })

  return (
    <div data-testid={`${testIdPrefix}-grouped-cell`}>
      {isIdentical ? (
        <Text type="secondary" data-testid={`${testIdPrefix}-identical`}>
          {identicalMessage}
        </Text>
      ) : (
        <Text data-testid={`${testIdPrefix}-summary`}>
          {summaryText}
          {hasDiff && (
            <Text type="secondary">
              {" · "}
              {t("documentSync.groupedComparison.changesCount", {
                count: diff!.length
              })}
            </Text>
          )}
        </Text>
      )}

      <div className="flex gap-3 mt-1">
        {hasDiff && (
          <button
            type="button"
            className="text-xs text-blue-600 bg-transparent border-0 p-0 cursor-pointer hover:underline"
            aria-expanded={showChanges}
            onClick={() => setShowChanges((v) => !v)}
            data-testid={`${testIdPrefix}-view-changes-toggle`}
          >
            {showChanges
              ? t("documentSync.groupedComparison.hideChanges")
              : t("documentSync.groupedComparison.viewChanges")}
          </button>
        )}
        <button
          type="button"
          className="text-xs text-gray-500 bg-transparent border-0 p-0 cursor-pointer hover:underline"
          aria-expanded={showAll}
          onClick={() => setShowAll((v) => !v)}
          data-testid={`${testIdPrefix}-view-all-toggle`}
        >
          {showAll
            ? t("documentSync.groupedComparison.hideAll", { item: itemNounPlural })
            : t("documentSync.groupedComparison.viewAll", { item: itemNounPlural })}
        </button>
      </div>

      {showChanges && hasDiff && (
        <ul
          className="mt-2 max-h-[350px] overflow-y-auto list-none m-0 p-0 space-y-1 border-t border-gray-100 pt-2"
          data-testid={`${testIdPrefix}-changes-list`}
        >
          {diff!.map((entry) => (
            <li key={entry.id} data-testid={`${testIdPrefix}-diff-${entry.id}`}>
              <DiffLine entry={entry} />
            </li>
          ))}
        </ul>
      )}

      {showAll && (
        <div
          className="mt-2 max-h-[350px] overflow-y-auto border-t border-gray-100 pt-2"
          data-testid={`${testIdPrefix}-all-groups`}
        >
          {groups.map((group) => {
            const isOpen = openGroupIds.has(group.id)
            return (
              <div key={group.id} className="mb-2 last:mb-0">
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-medium text-gray-700 bg-transparent border-0 p-0 cursor-pointer"
                  aria-expanded={isOpen}
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`${testIdPrefix}-group-toggle-${group.id}`}
                >
                  {isOpen ? <DownOutlined /> : <RightOutlined />}
                  {t("documentSync.groupedComparison.groupHeader", {
                    groupName: group.name,
                    count: group.items.length,
                    item:
                      group.items.length === 1 ? itemNounSingular : itemNounPlural
                  })}
                </button>
                {isOpen && (
                  <ul className="list-disc pl-5 mt-1 space-y-0.5">
                    {group.items.map((item) => (
                      <li
                        key={item.id}
                        data-testid={`${testIdPrefix}-item-${item.id}`}
                      >
                        <Text>{item.label}</Text>
                        {item.detail && (
                          <Text type="secondary"> — {item.detail}</Text>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DiffLine({ entry }: { entry: ComparisonDiffEntry }) {
  if (entry.status === "added" && entry.after) {
    return (
      <Text className="text-green-700">
        + {entry.after.label}
        {entry.after.detail ? ` — ${entry.after.detail}` : ""}
      </Text>
    )
  }

  if (entry.status === "removed" && entry.before) {
    return (
      <Text className="text-red-700 line-through">
        − {entry.before.label}
        {entry.before.detail ? ` — ${entry.before.detail}` : ""}
      </Text>
    )
  }

  if (entry.status === "changed" && entry.before && entry.after) {
    const labelChanged = entry.before.label !== entry.after.label
    const detailChanged = entry.before.detail !== entry.after.detail

    return (
      <Text className="text-amber-700">
        ~{" "}
        {labelChanged
          ? `${entry.before.label} → ${entry.after.label}`
          : entry.after.label}
        {detailChanged && (
          <>
            : {entry.before.detail ?? "—"} → {entry.after.detail ?? "—"}
          </>
        )}
      </Text>
    )
  }

  return null
}

export { GroupedComparisonCell }
